import React from 'react';
import { motion } from 'framer-motion';
import IndustryPageHeading from '../components/IndustryPageHeading';
import EnterpriseVideo from '../components/EnterpriseVideo';

/**
 * Logistics Solutions — internal route /solutions/industries/logistics
 *
 * Six content sections with alternating layout direction (text/video),
 * built strictly with GoogLyn's existing brand system: midnight navy,
 * champagne gold (#D4AF37), primary pink (#E8A0B8), sage green, and cream.
 * Section backgrounds alternate white / #F5F7F5 to match the Enterprise
 * Solutions and Products page rhythm. The site's standard footer is
 * rendered globally by Layout and is intentionally left untouched.
 */

const VideoDemoFrame = ({ cloudflareVideoId }) => {
  if (!cloudflareVideoId) {
    return (
      <div className="w-full">
        <div
          className="relative w-full overflow-hidden rounded-lg bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7]"
          style={{
            aspectRatio: '16 / 9',
            border: '2px solid #D4AF37',
            boxShadow:
              'inset 0 2px 8px rgba(20, 30, 48, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
          }}
          data-testid="logistics-video-placeholder"
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-base sm:text-lg text-black/60 font-medium tracking-wide">
              Video Demo Coming Soon
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <EnterpriseVideo
        cloudflareVideoId={cloudflareVideoId}
        title="Logistics demo video"
        testId={`logistics-video-${cloudflareVideoId}`}
        className="w-full rounded-lg bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7]"
        style={{
          aspectRatio: '16 / 9',
          border: '2px solid #D4AF37',
          boxShadow:
            'inset 0 2px 8px rgba(20, 30, 48, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      />
    </div>
  );
};

const FeatureBlock = ({ title, description }) => (
  <div className="pl-4 relative">
    <div
      className="absolute left-0 top-1 bottom-1 w-0.5 rounded-full"
      style={{
        background: 'linear-gradient(to bottom, #D4AF37, rgba(212, 175, 55, 0.4))',
      }}
    />
    <h3 className="text-base sm:text-lg font-medium text-black/90">{title}</h3>
    <p className="text-sm text-black/70 mt-1">{description}</p>
  </div>
);

const TextContent = ({ eyebrow, heading, body, features, isPageHeading }) => {
  const HeadingTag = isPageHeading ? 'h1' : 'h2';
  return (
    <div>
      <p className="text-sm md:text-base font-bold text-black/90 mb-3 md:mb-4 uppercase tracking-wider">
        {eyebrow}
      </p>
      <HeadingTag className="text-3xl md:text-4xl font-medium text-black/90 mb-4 md:mb-6">
        {heading}
      </HeadingTag>
      <p className="text-base sm:text-lg text-black/70 mb-8 md:mb-10">{body}</p>
      <div className="space-y-6">
        {features.map((f) => (
          <FeatureBlock key={f.title} title={f.title} description={f.description} />
        ))}
      </div>
    </div>
  );
};

const sections = [
  {
    eyebrow: 'IT Infrastructure & Architecture',
    heading: 'Built to plug into the systems you already run.',
    body:
      'GoogLyn\u2019s AI integrates directly with your existing EDI and API infrastructure \u2014 no rip-and-replace, no months-long IT migration.',
    cloudflareVideoId: '7be0843e2a6d6d2c6c5ae5296ec4087c',
    features: [
      {
        title: 'Seamless EDI/API integration',
        description:
          'Connect to your TMS, WMS, and carrier networks without disrupting existing data flows',
      },
      {
        title: 'Zero-downtime rollout',
        description: 'Deploy alongside current systems, then scale up on your timeline',
      },
      {
        title: 'Enterprise-grade security',
        description: 'Data stays encrypted and compliant at every integration point',
      },
    ],
  },
  {
    eyebrow: 'Sales',
    heading: 'From inbox to quote, instantly.',
    body:
      'GoogLyn automates the entire quoting process \u2014 capturing incoming rate requests by email, calculating pricing, and sending a finished quote back to the customer, without a rep touching a keyboard.',
    cloudflareVideoId: '4910dcb36e80c25c937fd9e263da39b7',
    features: [
      {
        title: 'Automated email intake',
        description: 'Reads incoming quote requests and extracts load details automatically',
      },
      {
        title: 'Instant pricing & quote generation',
        description: 'Calculates rates and builds a ready-to-send quote in seconds',
      },
      {
        title: 'Auto-reply to customer',
        description:
          'Sends the completed quote straight back to the customer\u2019s inbox, no manual follow-up needed',
      },
    ],
  },
  {
    eyebrow: 'Operations',
    heading: 'One shipment tracker, total accountability.',
    body:
      'Give every stakeholder \u2014 carrier, shipper, customer, and your own team \u2014 a single source of truth for every shipment, from booking to delivery.',
    cloudflareVideoId: 'e61e2629b0eaeaead1f7f8517ae8d514',
    features: [
      {
        title: 'End-to-end shipment tracking',
        description: 'Every load\u2019s status visible to everyone who needs it, in real time',
      },
      {
        title: 'Built-in accountability',
        description: 'Every update, call, and change logged against the shipment record',
      },
      {
        title: 'Fewer status-check calls',
        description: 'Stakeholders self-serve instead of calling in for updates',
      },
    ],
  },
  {
    eyebrow: 'Customer Service',
    heading: 'Every call answered, every dashboard live.',
    body:
      'A GoogLyn AI voice agent handles inbound and outbound calls around the clock \u2014 load status, ETA questions, appointment changes \u2014 while a live operations dashboard gives your team full visibility without lifting a phone.',
    cloudflareVideoId: 'a98f20a56597bf6026d30ac249bbc1b1',
    features: [
      {
        title: 'AI voice agent, 24/7',
        description: 'Handles booking, tracking, and status calls in a natural, human-like voice',
      },
      {
        title: 'Real-time operations dashboard',
        description: 'See call volume, resolution rates, and open issues at a glance',
      },
      {
        title: 'Instant escalation',
        description:
          'Anything outside the agent\u2019s scope routes straight to your team, with full context attached',
      },
    ],
  },
  {
    eyebrow: 'Finance',
    heading: 'Invoices processed while you sleep.',
    body:
      'GoogLyn automates invoice matching, discrepancy flags, and reconciliation, so your finance team stops chasing paperwork and starts closing books faster.',
    cloudflareVideoId: '4c23dd0a695e74fcea8af0aad3308cd4',
    features: [
      {
        title: 'Automated invoice matching',
        description: 'Cross-checks invoices against confirmed rates instantly',
      },
      {
        title: 'Discrepancy flagging',
        description: 'Catches mismatches and routes exceptions before they age',
      },
      {
        title: 'Faster month-end close',
        description: 'Reconciliation that used to take days finishes overnight',
      },
    ],
  },
  {
    eyebrow: 'Reporting & Analysis',
    heading: 'See where every dollar goes.',
    body:
      'Reconciliation reports, revenue leakage detection, and detention control \u2014 all surfaced automatically, so nothing slips through the cracks.',
    cloudflareVideoId: '7386538a34bc967c6f05ed3a323db447',
    features: [
      {
        title: 'Reconciliation reports',
        description: 'Automatically generated and delivered on your schedule',
      },
      {
        title: 'Revenue leakage detection',
        description: 'Flags underbilling, missed accessorials, and rate mismatches',
      },
      {
        title: 'Detention control',
        description: 'Tracks detention time and flags billable delays before they\u2019re missed',
      },
    ],
  },
];

const LogisticsSolutions = () => {
  return (
    <div className="w-full" data-testid="logistics-solutions-page">
      <IndustryPageHeading name="Logistics" testId="logistics-page-heading" />
      {sections.map((section, idx) => {
        const textFirst = idx % 2 === 0; // sections 1,3,5 → text left; 2,4 → video left
        const bg = idx % 2 === 0 ? 'bg-white' : 'bg-[#F5F7F5]';
        return (
          <section
            key={section.eyebrow}
            className={`py-16 md:py-24 ${bg}`}
            data-testid={`logistics-section-${idx + 1}`}
          >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center"
              >
                {textFirst ? (
                  <>
                    <TextContent {...section} isPageHeading={idx === 0} />
                    <VideoDemoFrame cloudflareVideoId={section.cloudflareVideoId} />
                  </>
                ) : (
                  <>
                    {/* Video first on desktop; text stays above video on mobile for readability */}
                    <div className="order-2 lg:order-1">
                      <VideoDemoFrame cloudflareVideoId={section.cloudflareVideoId} />
                    </div>
                    <div className="order-1 lg:order-2">
                      <TextContent {...section} isPageHeading={idx === 0} />
                    </div>
                  </>
                )}
              </motion.div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default LogisticsSolutions;
